import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  type FiltroNotifiche,
  type PrioritaNotifica,
  useMarkLetta,
  useMarkTutteLette,
  useNotificheList,
} from "../../hooks/useNotifiche";
import { MobileShell } from "../shell/MobileShell";
import { etichetta } from "../common/etichette";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";

const PRIORITY_LABEL: Record<PrioritaNotifica, string> = {
  bassa: "BASSA",
  media: "MEDIA",
  alta: "ALTA",
  urgente: "URGENTE",
};

const PRIORITY_PILL: Record<PrioritaNotifica, string> = {
  bassa: "bg-fg-muted/20 text-fg-muted",
  media: "bg-info/20 text-info",
  alta: "bg-warning/20 text-warning",
  urgente: "bg-danger/20 text-danger",
};

const TABS: { value: FiltroNotifiche; label: string }[] = [
  { value: "tutte", label: tr("Tutte") },
  { value: "non_lette", label: tr("Non lette") },
  { value: "lette", label: tr("Lette") },
];

export function NotifichePage() {
  useAuth(); // session check
  const [filtro, setFiltro] = useState<FiltroNotifiche>("tutte");
  const { data, isLoading } = useNotificheList(filtro, 100);
  const markLetta = useMarkLetta();
  const markTutte = useMarkTutteLette();

  const items = data?.items ?? [];

  return (
    <MobileShell logoSuffix="NOTIFICHE">
      {/* Header + tabs */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Notifiche")}</h2>
        <button
          type="button"
          onClick={() => markTutte.mutate()}
          disabled={markTutte.isPending}
          className="btn-ghost text-sm"
        >
          {markTutte.isPending ? "…" : tr("✓ Segna tutte come lette")}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-primary/20">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setFiltro(t.value)}
            className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${
              filtro === t.value
                ? "border-accent text-accent"
                : "border-transparent text-fg-muted hover:text-fg"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Lista */}
      <div className="card p-0 overflow-hidden">
        {isLoading && <div className="p-6 text-center text-fg-muted">{tr("Caricamento…")}</div>}
        {!isLoading && items.length === 0 && (
          <div className="p-8 text-center text-fg-muted">
            Nessuna notifica
            {filtro === "non_lette" ? tr(" non letta") : filtro === "lette" ? tr(" letta") : ""}.
          </div>
        )}
        {items.map((n) => {
          const onClick = () => {
            if (!n.letta) markLetta.mutate(n.id);
          };
          const inner = (
            <div
              onClick={onClick}
              className={`flex gap-4 p-4 border-b border-primary/10 last:border-b-0 hover:bg-primary/10 transition-colors cursor-pointer ${
                !n.letta ? "bg-accent/5" : ""
              }`}
            >
              <div className="flex-shrink-0">
                <span
                  className={`pill ${PRIORITY_PILL[n.priorita]}`}
                  title={tr("Priorità {{v1}}", { v1: n.priorita })}
                >
                  {PRIORITY_LABEL[n.priorita]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-bold text-fg">{tr(n.titolo)}</div>
                  {!n.letta && (
                    <span className="text-[10px] uppercase tracking-wider font-bold text-accent flex-shrink-0">
                      {tr("● nuova")}
                    </span>
                  )}
                </div>
                {n.messaggio && <div className="text-sm text-fg-muted mt-1">{tr(n.messaggio)}</div>}
                <div className="text-xs text-fg-muted mt-2 font-mono">
                  {new Date(n.created_at).toLocaleString(localeCorrente())} ·{" "}
                  {etichetta(n.evento_tipo)}
                  {n.letta_at && (
                    <> · letta {new Date(n.letta_at).toLocaleString(localeCorrente())}</>
                  )}
                </div>
              </div>
            </div>
          );
          return n.link_app ? (
            <Link key={n.id} to={n.link_app} className="block">
              {inner}
            </Link>
          ) : (
            <div key={n.id}>{inner}</div>
          );
        })}
      </div>

      {data?.has_more && (
        <p className="text-xs text-fg-muted text-center">
          {tr("Sono mostrate le 100 notifiche più recenti.")}
        </p>
      )}
    </MobileShell>
  );
}
